
import { Loader2, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminDashboard } from "@/hooks/useAdminDashboard";
import { DashboardStats } from "@/components/admin/DashboardStats";
import { CancellationRequestsTab } from "@/components/admin/CancellationRequestsTab";
import { RecentBookingsTab } from "@/components/admin/RecentBookingsTab";
import { ParkingSpotsTab } from "@/components/admin/ParkingSpotsTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { Button } from "@/components/ui/button";
import { exportToExcel, fetchAllDataForExport } from "@/utils/admin-utils";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { AnalyticsCharts } from "@/components/admin/AnalyticsCharts";
import { RevenueMetrics } from "@/components/admin/RevenueMetrics";
import { UserActivityMetrics } from "@/components/admin/UserActivityMetrics";
import { LiveOpsTab } from "@/components/admin/ops/LiveOpsTab";
import { AnnouncementsTab } from "@/components/admin/ops/AnnouncementsTab";
import { AuditLogTab } from "@/components/admin/ops/AuditLogTab";
import { AbuseSignalsTab } from "@/components/admin/ops/AbuseSignalsTab";
import CampaignsTab from "@/components/admin/growth/CampaignsTab";
import PromoCodesTab from "@/components/admin/growth/PromoCodesTab";
import GrowthAnalyticsTab from "@/components/admin/growth/GrowthAnalyticsTab";

const AdminDashboard = () => {
  const {
    isAdmin,
    loading,
    stats,
    cancellationRequests,
    recentBookings,
    parkingSpots,
    users,
    handleCancellationAction
  } = useAdminDashboard();

  const [exporting, setExporting] = useState(false);

  if (!isAdmin) {
    return (
      <div>
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-4">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <div className="container mx-auto px-4 py-8 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  const handleExportCurrentData = () => {
    const dataToExport = {
      stats,
      cancellationRequests,
      recentBookings,
      parkingSpots,
      users
    };
    
    exportToExcel(dataToExport, 'admin-dashboard-data');
  };

  const handleExportAllData = async () => {
    try {
      setExporting(true);
      toast({
        title: "Starting export",
        description: "Fetching all data. This might take a moment...",
      });
      
      const allData = await fetchAllDataForExport();
      exportToExcel(allData, 'complete-system-data');
      
      toast({
        title: "Export completed",
        description: "All system data has been exported successfully.",
      });
    } catch (error) {
      console.error('Error exporting all data:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting the data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <div className="flex gap-3">
            <Button 
              onClick={handleExportCurrentData} 
              variant="outline" 
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Dashboard Data
            </Button>
            <Button 
              onClick={handleExportAllData} 
              variant="default" 
              className="flex items-center gap-2"
              disabled={exporting}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export All System Data
            </Button>
          </div>
        </div>
        
        <DashboardStats stats={stats} />

        <Tabs defaultValue="analytics" className="w-full mt-6">
          <TabsList className="w-full justify-start mb-6 overflow-x-auto flex-nowrap">
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="user-activity">User Activity</TabsTrigger>
            <TabsTrigger value="cancellations">Cancellation Requests</TabsTrigger>
            <TabsTrigger value="bookings">Recent Bookings</TabsTrigger>
            <TabsTrigger value="spots">Parking Spots</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="live-ops">Live Ops</TabsTrigger>
            <TabsTrigger value="announcements">Announcements</TabsTrigger>
            <TabsTrigger value="abuse">Abuse Signals</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
            <TabsTrigger value="growth">Growth</TabsTrigger>
            <TabsTrigger value="promos">Promo Codes</TabsTrigger>
            <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsCharts />
          </TabsContent>

          <TabsContent value="revenue" className="mt-6">
            <RevenueMetrics />
          </TabsContent>

          <TabsContent value="user-activity" className="mt-6">
            <UserActivityMetrics />
          </TabsContent>

          <TabsContent value="cancellations" className="mt-6">
            <CancellationRequestsTab
              requests={cancellationRequests}
              onAction={handleCancellationAction}
            />
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <RecentBookingsTab bookings={recentBookings} />
          </TabsContent>

          <TabsContent value="spots" className="mt-6">
            <ParkingSpotsTab spots={parkingSpots} />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <UsersTab users={users} />
          </TabsContent>

          <TabsContent value="live-ops" className="mt-6">
            <LiveOpsTab />
          </TabsContent>
          <TabsContent value="announcements" className="mt-6">
            <AnnouncementsTab />
          </TabsContent>
          <TabsContent value="abuse" className="mt-6">
            <AbuseSignalsTab />
          </TabsContent>
          <TabsContent value="audit" className="mt-6">
            <AuditLogTab />
          </TabsContent>
          <TabsContent value="growth" className="mt-6">
            <GrowthAnalyticsTab />
          </TabsContent>
          <TabsContent value="promos" className="mt-6">
            <PromoCodesTab />
          </TabsContent>
          <TabsContent value="campaigns" className="mt-6">
            <CampaignsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
